/**
 * Monotonically increasing signal ID generator.
 *
 * IDs are used as stable keys for:
 *   - Dependency deduplication (bitfield mask where size allows)
 *   - DevTools identity tracking
 *   - Scheduler deduplication set
 *
 * WeakMap<object, number> maps signal container object → its integer ID without
 * preventing GC of the container (no strong reference).
 */

let _nextId = 1

/** Returns the next unique signal ID. Thread-safe for single-threaded JS. */
export function nextSignalId(): number {
    return _nextId++
}

/** Returns the current counter value (for tests / diagnostics). */
export function peekSignalIdCounter(): number {
    return _nextId
}

/** @internal — test-only reset to make tests deterministic. */
export function __resetSignalIdCounter(): void {
    _nextId = 1
}

/** WeakMap from signal container object → its assigned ID. */
const signalIdMap = new WeakMap<object, number>()

/**
 * Get or assign a stable ID for a signal container.
 * Idempotent: calling multiple times returns same ID.
 */
export function getOrAssignSignalId(signal: object): number {
    let id = signalIdMap.get(signal)
    if (id === undefined) {
        id = nextSignalId()
        signalIdMap.set(signal, id)
    }
    return id
}

/**
 * Look up an already-assigned ID. Returns -1 if not found.
 * Used to test whether a signal has been registered.
 */
export function lookupSignalId(signal: object): number {
    return signalIdMap.get(signal) ?? -1
}
